import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, ref } from 'vue';

import CountdownTimer from '@/components/sponsored-recommendations/actions/CountdownTimer.vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import {
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';

import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { useCampaignInfo } from '@/components/sponsored-recommendations/useSendBidId';

import { SraEventAction } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { Maybe } from '@/helpers/types';
import { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';
import { createReusableTemplate } from '@vueuse/core';

/** Viewing thresholds at which we want to track. */
type TrackingWatchedPoints = (typeof TrackingWatchedPoints)[number];
type WebKitHTMLVideoElement =
	| HTMLVideoElement
	// To support mobile safari browsers
	| (HTMLVideoElement & { webkitSupportsFullscreen: true; webkitEnterFullscreen: () => void });


export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationPremiumVideo',
  props: {
    sponsoredAd: null,
    lazy: { type: Boolean, default: true },
    additionalContexts: { default: () => [] }
  },
  emits: ["onPosterClickOut", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: NonNullable<SponsoredAdFragment>, lazy: boolean, additionalContexts: (SnowplowModuleContext | SnowplowContext)[] };

const TrackingWatchedPoints = [0, 25, 50, 75, 90, 100] as const;





const { campaignNode, promotionalTitle, promotionalTextWithGenreFallback } = useCampaignInfo(
	() => props.sponsoredAd.campaign
);

const { trackSraEvent } = useBackendTracking();

const disclaimerText = computed(() => props.sponsoredAd.campaign?.disclaimerText);

const getReleaseDate = computed<Date | undefined>(() => {
	if (!process.client || !props.sponsoredAd.campaign?.countdownTimer) return undefined;

	return new Date(props.sponsoredAd.campaign?.countdownTimer);
});

// Title Poster (to support AVIF)
const posterSRImage = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAd.campaign?.posterOverride || campaignNode?.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${JW_CONFIG.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

// Manage check to display countdown
onMounted(() => checkIfHasTimeRemaining());
onUnmounted(() => clearInterval(timerToMatchStarts));
const timerToMatchStarts = setInterval(checkIfHasTimeRemaining, 1000);

const hasTimeRemaining = ref(true);

function onVolumeChange(event: Event) {
	const change = (event.target as HTMLVideoElement).muted ? 'muted' : 'unmuted';
	isMuted.value = change === 'muted';

	if (!campaignNode.value) {
		return;
	}

	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_mute',
			value: isMuted.value ? 1 : 0,
		},
		[titleContext.value]
	);
}

function checkIfHasTimeRemaining() {
	// If there is no release time, hide countdown
	if (!process.client || !getReleaseDate.value) {
		hasTimeRemaining.value = false;
		return;
	}

	const currentTime = new Date();

	// If release time is after the currentTime
	if (currentTime.getTime() >= getReleaseDate.value.getTime()) {
		hasTimeRemaining.value = false;
		return;
	}

	hasTimeRemaining.value = true;
}

// Manage Video

const videoElement = ref<WebKitHTMLVideoElement | null>(null);
const isMuted = ref(videoElement.value?.muted ?? true);

const { define: DefineVideoControls, reuse: ReuseVideoControls } = createReusableTemplate();

const volumeIcon = computed(() => {
	const filename = isMuted.value ? 'volume-xmark' : 'volume-high';
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
});

const titleContext = computed(() => {
	return new SnowplowTitleContextGraphql(
		campaignNode.value?.objectId,
		campaignNode.value?.objectType,
		campaignNode.value?.__typename === 'Season' ? campaignNode.value?.content.seasonNumber : null
	);
});

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPoint = ref<TrackingWatchedPoints | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	if (lastTrackedPoint.value >= 100) return;

	const percentageViewed = Math.ceil(((videoElement.value?.currentTime ?? 0) / videoDuration.value) * 100);
	const nextTrackingPoint = TrackingWatchedPoints.find(point => point > lastTrackedPoint.value);

	if (nextTrackingPoint == null || percentageViewed < nextTrackingPoint) return;

	lastTrackedPoint.value = nextTrackingPoint;

	trackSraEvent({
		action: SraEventAction.VideoView,
		sponsoredAd: () => props.sponsoredAd,
		progress: nextTrackingPoint,
	});
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;
	videoElement.value.play();
}

const fullscreenIcon = computed(() => {
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/fullscreen.svg`;
});

/** This only handles entering fullscreen and not exiting, because there are
 * only native controls in fullscreen */
async function requestFullscreen() {
	try {
		if (!videoElement.value) return;

		if ('webkitSupportsFullscreen' in videoElement.value && videoElement.value.webkitSupportsFullscreen === true) {
			videoElement.value.webkitEnterFullscreen();
		}

		await videoElement.value.requestFullscreen();
	} catch (error) {
		// Nothing to do here
	}
}

function onChangeFullscreen() {
	const change = Boolean(document.fullscreenElement) ? 'entered' : 'exited';
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_fullscreen',
			value: change === 'entered' ? 1 : 0,
		},
		[titleContext.value]
	);
}

return { __sfc: true,TrackingWatchedPoints, props, emits, campaignNode, promotionalTitle, promotionalTextWithGenreFallback, trackSraEvent, disclaimerText, getReleaseDate, posterSRImage, timerToMatchStarts, hasTimeRemaining, onVolumeChange, checkIfHasTimeRemaining, videoElement, isMuted, DefineVideoControls, ReuseVideoControls, volumeIcon, titleContext, toggleMute, videoDuration, lastTrackedPoint, setVideoDuration, onTimeUpdate, onVideoEnded, fullscreenIcon, requestFullscreen, onChangeFullscreen, CountdownTimer, PictureComp }
}

})